import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  StarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';

interface Survey {
  _id: string;
  number: string;
  shortDescription: string;
  templateId?: {
    _id: string;
    name: string;
    questions: Array<{
      question: string;
      type: 'rating' | 'text' | 'yesno';
      required: boolean;
    }>;
  };
  respondentId?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  state: string;
  responses?: any;
  score?: number;
  relatedTaskId?: string;
  relatedTaskType?: string;
  completedAt?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

const SurveyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDate, formatDateTime, preferences } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Custom formatter that always includes AM/PM for this page
  const formatDateTimeWithAMPM = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return '-';
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    
    try {
      // Format date part using user preferences
      const datePart = formatDate(dateInput);
      
      // Format time part with AM/PM (force 12-hour format)
      const timeOptions: Intl.DateTimeFormatOptions = {
        timeZone: preferences.timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true, // Force 12-hour format with AM/PM
      };
      
      const timePart = date.toLocaleTimeString('en-US', timeOptions);
      
      return `${datePart} ${timePart}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return date.toISOString();
    }
  };
  const [responses, setResponses] = useState<Record<number, any>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});

  const { data: survey, isLoading, error } = useQuery<Survey>({
    queryKey: ['survey', id],
    queryFn: async () => {
      const response = await api.get(`/surveys/${id}`);
      return response.data.survey;
    }
  });

  const submitResponseMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post(`/surveys/${id}/respond`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey', id] });
    },
    onError: (error: any) => {
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      }
    }
  });

  const handleResponseChange = (questionIndex: number, value: any) => {
    setResponses(prev => ({ ...prev, [questionIndex]: value }));
    // Clear error for this question
    if (errors[questionIndex]) {
      setErrors(prev => ({ ...prev, [questionIndex]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!survey?.templateId?.questions) return;

    // Validate required questions
    const newErrors: Record<number, string> = {};
    survey.templateId.questions.forEach((question: any, index: number) => {
      if (question.required && !responses[index]) {
        newErrors[index] = 'This question is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Calculate score based on rating questions
    let totalRating = 0;
    let ratingCount = 0;
    
    survey.templateId.questions.forEach((question: any, index: number) => {
      if (question.type === 'rating' && responses[index]) {
        totalRating += parseInt(responses[index]);
        ratingCount++;
      }
    });

    const score = ratingCount > 0 ? totalRating / ratingCount : 0;

    // Convert responses from index-based to questionId-based for API
    const responsesByQuestionId: Record<string, any> = {};
    survey.templateId.questions.forEach((question: any, index: number) => {
      if (responses[index] !== undefined && responses[index] !== null) {
        responsesByQuestionId[question.id] = responses[index];
      }
    });

    submitResponseMutation.mutate({
      responses: responsesByQuestionId,
      score
    });
  };

  const renderQuestion = (question: any, index: number) => {
    switch (question.type) {
      case 'rating':
        return (
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => handleResponseChange(index, rating)}
                className="p-1 focus:outline-none"
              >
                {responses[index] >= rating ? (
                  <StarSolidIcon className="h-8 w-8 text-yellow-400" />
                ) : (
                  <StarIcon className="h-8 w-8 text-gray-300 hover:text-yellow-400" />
                )}
              </button>
            ))}
          </div>
        );

      case 'yesno':
        return (
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={`question-${index}`}
                value="yes"
                checked={responses[index] === 'yes'}
                onChange={(e) => handleResponseChange(index, e.target.value)}
                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`question-${index}`}
                value="no"
                checked={responses[index] === 'no'}
                onChange={(e) => handleResponseChange(index, e.target.value)}
                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700">No</span>
            </label>
          </div>
        );

      case 'text':
        return (
          <textarea
            value={responses[index] || ''}
            onChange={(e) => handleResponseChange(index, e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Enter your response..."
          />
        );

      default:
        return null;
    }
  };

  const renderResponseView = (question: any, response: any) => {
    // Handle null/undefined responses
    if (response === null || response === undefined || response === '') {
      return <p className="text-sm text-gray-500 italic">No response provided</p>;
    }

    switch (question.type) {
      case 'rating':
        const ratingValue = typeof response === 'number' ? response : parseInt(response, 10);
        if (isNaN(ratingValue)) {
          return <p className="text-gray-700">{response}</p>;
        }
        return (
          <div className="flex items-center">
            {[1, 2, 3, 4, 5].map((rating) => (
              <StarSolidIcon
                key={rating}
                className={`h-5 w-5 ${rating <= ratingValue ? 'text-yellow-400' : 'text-gray-300'}`}
              />
            ))}
            <span className="ml-2 text-gray-600">({ratingValue}/5)</span>
          </div>
        );

      case 'yesno':
      case 'boolean':
        const yesNoValue = typeof response === 'string' ? response.toLowerCase() : String(response);
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            yesNoValue === 'yes' || yesNoValue === 'true' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {yesNoValue === 'yes' || yesNoValue === 'true' ? 'Yes' : 'No'}
          </span>
        );

      case 'choice':
        // Single choice - display the selected option
        // 5-point rating scale: Excellent=5, Good=4, Neutral=3, Poor=2, Very Poor=1
        const choiceValue = String(response).toLowerCase();
        const getChoiceNumericValue = (choice: string): number | null => {
          if (choice === 'excellent') return 5;
          if (choice === 'good') return 4;
          if (choice === 'neutral') return 3;
          if (choice === 'poor') return 2;
          if (choice === 'very poor') return 1;
          return null;
        };
        const numericValue = getChoiceNumericValue(choiceValue);
        
        if (numericValue !== null) {
          return (
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-3">
                <div className="inline-flex items-center px-3 py-1 rounded-md bg-blue-50 text-blue-800 text-sm font-medium">
                  {response}
                </div>
                <span className="text-sm font-medium text-gray-700">{numericValue}/5</span>
              </div>
              <div className="flex items-center space-x-0.5">
                {renderStars(numericValue)}
              </div>
            </div>
          );
        }
        
        // Fallback for other choice values
        return (
          <div className="inline-flex items-center px-3 py-1 rounded-md bg-blue-50 text-blue-800 text-sm font-medium">
            {response}
          </div>
        );

      case 'multiple_choice':
        // Multiple choice - response might be comma-separated or array
        const choices = Array.isArray(response) 
          ? response 
          : typeof response === 'string' 
            ? response.split(',').map(c => c.trim()).filter(c => c)
            : [String(response)];
        
        return (
          <div className="flex flex-wrap gap-2">
            {choices.map((choice: string, idx: number) => (
              <span
                key={idx}
                className="inline-flex items-center px-3 py-1 rounded-md bg-blue-50 text-blue-800 text-sm font-medium"
              >
                {choice}
              </span>
            ))}
          </div>
        );

      case 'text':
        return (
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-gray-700 whitespace-pre-wrap">{response}</p>
          </div>
        );

      default:
        // Fallback for unknown types - just display the raw response
        return (
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-gray-700">{String(response)}</p>
          </div>
        );
    }
  };


  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarSolidIcon key={i} className="h-4 w-4 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarIcon className="h-4 w-4 text-gray-300" />
            <StarSolidIcon className="h-4 w-4 text-yellow-400 absolute top-0 left-0" style={{ clipPath: 'inset(0 50% 0 0)' }} />
          </div>
        );
      } else {
        stars.push(
          <StarIcon key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }
    return stars;
  };

  // Render questions in read-only mode (for 'sent' state)
  const renderReadOnlyQuestion = (question: any, index: number) => {
    return (
      <div key={question.id || index} className="space-y-2 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
        <p className="text-sm font-medium text-gray-700">
          {index + 1}. {question.question}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </p>
        <div className="mt-2">
          <p className="text-sm text-gray-500 italic">Not yet answered</p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Handle errors
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading survey. Please try again.</p>
        </div>
      </div>
    );
  }

  // If no survey data and not loading, show error
  if (!survey && !isLoading) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Survey not found.</p>
        </div>
      </div>
    );
  }

  // Safety check: if survey is still null after loading, return early
  if (!survey) {
    return null;
  }

  // Get questions from templateId (API returns templateId, not template)
  // Questions are already ordered by the backend (orderBy: { order: 'asc' })
  const questions = survey.templateId?.questions || [];
  const hasResponses = survey.responses && typeof survey.responses === 'object' && Object.keys(survey.responses).length > 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/surveys')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Surveys
        </button>
      </div>

      {/* Survey Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{survey.number}</h1>
              <p className="mt-1 text-lg text-gray-600">{stripHtml(survey.shortDescription)}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              survey.state === 'sent' ? 'bg-blue-100 text-blue-800' :
              survey.state === 'completed' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {survey.state.charAt(0).toUpperCase() + survey.state.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Survey Content */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {survey.templateId?.name || 'Survey Questions'}
          </h2>
        </div>

        <div className="p-6">
          {/* State: expired - Show read-only questions and responses if any */}
          {survey.state === 'expired' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm font-medium text-yellow-900">
                  This survey has expired and can no longer be completed.
                </p>
                {survey.expiresAt && (
                  <p className="mt-1 text-sm text-yellow-700">
                    Expired on: {formatDateTime(survey.expiresAt)}
                  </p>
                )}
              </div>

              {hasResponses ? (
                // Show responses if available (similar to completed state)
                <>
                  {survey.score !== undefined && survey.score !== null && (
                    <div className="bg-indigo-50 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-indigo-900">Avg Satisfaction</span>
                        <div className="flex flex-col items-end space-y-1">
                          <span className="text-lg font-medium text-indigo-900">
                            {survey.score.toFixed(2)}/5
                          </span>
                          <div className="flex items-center space-x-0.5">
                            {renderStars(survey.score)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Question Responses</h3>
                    {questions.length > 0 ? (
                      questions.map((question: any, index: number) => {
                        const responseKey = index.toString();
                        const response = survey.responses?.[responseKey];
                        
                        return (
                          <div key={question.id || index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-900">
                                {index + 1}. {question.question}
                                {question.required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              <div className="mt-2">
                                {response !== undefined && response !== null && response !== '' ? (
                                  renderResponseView(question, response)
                                ) : (
                                  <p className="text-sm text-gray-500 italic">No response provided</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">No questions found in this survey template.</p>
                    )}
                  </div>
                </>
              ) : (
                // Show questions as read-only if no responses
                questions.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-900">Survey Questions</h3>
                    {questions.map((question: any, index: number) => 
                      renderReadOnlyQuestion(question, index)
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No questions found in this survey template.</p>
                )
              )}
            </div>
          )}

          {/* State: sent - Show read-only questions */}
          {survey.state === 'sent' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  Survey is sent to the customer, but not yet completed.
                </p>
              </div>

              {questions.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900">Survey Questions</h3>
                  {questions.map((question: any, index: number) => 
                    renderReadOnlyQuestion(question, index)
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No questions found in this survey template.</p>
              )}
            </div>
          )}

          {/* State: completed - Show responses and Avg Satisfaction */}
          {survey.state === 'completed' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">Survey Completed</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Thank you for your feedback! This survey was completed on{' '}
                      {survey.completedAt && formatDateTime(survey.completedAt)}
                    </p>
                  </div>
                </div>
              </div>

              {survey.score !== undefined && survey.score !== null && (
                <div className="bg-indigo-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-indigo-900">Avg Satisfaction</span>
                    <div className="flex flex-col items-end space-y-1">
                      <span className="text-lg font-medium text-indigo-900">
                        {survey.score.toFixed(2)}/5
                      </span>
                      <div className="flex items-center space-x-0.5">
                        {renderStars(survey.score)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Question Responses</h3>
                {questions.length > 0 ? (
                  questions.map((question: any, index: number) => {
                    const responseKey = index.toString();
                    const response = survey.responses?.[responseKey];
                    
                    return (
                      <div key={question.id || index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-900">
                            {index + 1}. {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <div className="mt-2">
                            {response !== undefined && response !== null && response !== '' ? (
                              renderResponseView(question, response)
                            ) : (
                              <p className="text-sm text-gray-500 italic">No response provided</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">No questions found in this survey template.</p>
                )}
              </div>
            </div>
          )}

          {/* Other states (new, in_progress, cancelled) - Show appropriate message */}
          {survey.state !== 'sent' && survey.state !== 'completed' && survey.state !== 'expired' && (
            <div className="text-center py-8">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {survey.state === 'cancelled' 
                  ? 'This survey has been cancelled.'
                  : survey.state === 'in_progress'
                  ? 'This survey is in progress.'
                  : 'No response data available'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Survey Details */}
      <div className="mt-6 bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Survey Details</h2>
        </div>
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Survey Number</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{survey.number}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {survey.state === 'sent' ? 'Sent' :
                 survey.state === 'completed' ? 'Completed' :
                 survey.state === 'expired' ? 'Expired' :
                 survey.state.charAt(0).toUpperCase() + survey.state.slice(1)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDateTimeWithAMPM(survey.createdAt)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {survey.state === 'completed' ? 'Completed At' : 'Survey Expire'}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {survey.state === 'completed' 
                  ? (survey.completedAt ? formatDateTimeWithAMPM(survey.completedAt) : '-')
                  : (survey.expiresAt ? formatDateTimeWithAMPM(survey.expiresAt) : '-')}
              </dd>
            </div>
            {survey.respondentId && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Respondent</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {survey.respondentId.firstName} {survey.respondentId.lastName}
                  <br />
                  <span className="text-gray-500">{survey.respondentId.email}</span>
                </dd>
              </div>
            )}
            {survey.relatedTaskType && survey.relatedTaskId && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Related To</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {survey.relatedTaskType.charAt(0).toUpperCase() + survey.relatedTaskType.slice(1)} #{survey.relatedTaskId.slice(-6)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default SurveyDetail;
